import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Axios } from "../../../Api/axios";
import { PUBLIC_GALLERY, GALLERY_PHOTO_URL } from "../../../Api/Api";
import slide1 from "../../../Assets/images/slide1.jpg";
import slide2 from "../../../Assets/images/slide2.jpg";
import slide3 from "../../../Assets/images/slide3.jpg";
import "./Landing.css";

const STATIC_SLIDES = [
  { id: "s1", img: slide1, sub: "نوفّر أفضل البيئات التعليمية لجميع المستويات الدراسية" },
  { id: "s2", img: slide2, sub: "تعلّم على يد نخبة من المعلمين ذوي الكفاءة العالية" },
  { id: "s3", img: slide3, sub: "انضم إلينا وحقّق أهدافك الأكاديمية بثقة ودعم مستمر" },
];

/* Title segments — gold flag marks the "أكاديمية" part */
const TITLE_SEGMENTS = [
  { text: "أكاديمية ", gold: true },
  { text: "التعليم", gold: false },
];
const FULL_TITLE = TITLE_SEGMENTS.map((s) => s.text).join("");

/* Builds an array of {char, gold} for efficient lookup */
const CHAR_MAP = TITLE_SEGMENTS.flatMap(({ text, gold }) =>
  Array.from(text).map((char) => ({ char, gold }))
);

function useTypewriter(animKey, speed = 70, startDelay = 400) {
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setCount(0);
    const startTimer = setTimeout(() => {
      timerRef.current = setInterval(() => {
        setCount((c) => {
          if (c >= CHAR_MAP.length) {
            clearInterval(timerRef.current);
            return c;
          }
          return c + 1;
        });
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      clearInterval(timerRef.current);
    };
  }, [animKey, speed, startDelay]);

  const done = count >= CHAR_MAP.length;
  return { count, done };
}

export default function Landing() {
  const [slides,     setSlides]     = useState(null);
  const [intervalMs] = useState(7000);
  const [current,   setCurrent]    = useState(0);
  const [animKey,   setAnimKey]    = useState(0);
  const { count: twCount, done: twDone } = useTypewriter(animKey, 160, 600);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToMaterials = useCallback((e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById("materials")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return;
    }
    document.getElementById("materials")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.pathname, navigate]);

  useEffect(() => {
    Axios.get(PUBLIC_GALLERY)
      .then((res) => {
        const images = res.data.images || [];
        setSlides(
          images.length > 0
            ? images.map((img) => ({ id: img.id, img: GALLERY_PHOTO_URL(img.photo), sub: img.title || "" }))
            : STATIC_SLIDES
        );
      })
      .catch(() => setSlides(STATIC_SLIDES));
  }, []);

  const displaySlides = slides ?? STATIC_SLIDES;

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    setAnimKey((k) => k + 1);
  }, []);

  /* Auto-advance */
  useEffect(() => {
    if (displaySlides.length <= 1) return;
    const t = setInterval(() => {
      setCurrent((c) => {
        const next = (c + 1) % displaySlides.length;
        setAnimKey((k) => k + 1);
        return next;
      });
    }, intervalMs);
    return () => clearInterval(t);
  }, [displaySlides.length, intervalMs]);

  const slide = displaySlides[current];

  return (
    <div className="hero-wrapper">

      {/* ── Full-bleed background ── */}
      <div className="hero-bg">
        <img key={slide.id} src={slide.img} alt="أكاديمية التعليم" className="hero-bg-img" />
        <div className="hero-bg-overlay" />
      </div>

      {/* ── Centered content ── */}
      <div className="hero-center">
        <div className="hero-content-in" key={animKey}>

          {/* Label */}
          <div className="hero-label">
            <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
            </svg>
            مؤسسة تعليمية
          </div>

          {/* Title — typewriter */}
          <h1 className="hero-title">
            {CHAR_MAP.slice(0, twCount).map(({ char, gold }, i) =>
              gold
                ? <span key={i} className="hero-title-gold">{char}</span>
                : <span key={i}>{char}</span>
            )}
            <span className={`tw-cursor${twDone ? " done" : ""}`} />
          </h1>

          {/* Divider */}
          <div className="hero-divider" />

          {/* Subtitle */}
          <p className="hero-subtitle">
            {slide.sub || "نوفّر أفضل البيئات التعليمية لجميع المستويات الدراسية"}
          </p>

          {/* CTA */}
          <div className="hero-btns">
            <a href="#materials" onClick={scrollToMaterials} className="hero-btn-primary">
              تصفّح الدورات
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z"/>
              </svg>
            </a>
            <Link to="/about" className="hero-btn-secondary">
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
              </svg>
              تعرّف علينا
            </Link>
          </div>
        </div>

        {/* Dots */}
        {displaySlides.length > 1 && (
          <div className="hero-dots">
            {displaySlides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`hero-dot${i === current ? " active" : ""}`}
                aria-label={`slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scroll hint */}
      <div className="scroll-hint">
        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
        <span>أسفل</span>
      </div>

    </div>
  );
}
